import { useState, useCallback, useRef, TouchEvent } from 'react';

export type DrawerPosition = 'collapsed' | 'half' | 'expanded';

export interface DrawerHeights {
  collapsed: number;
  half: number;
  expanded: number;
}

const DEFAULT_DRAWER_HEIGHTS: DrawerHeights = {
  collapsed: 21,    // 21vh - visible handle on Safari with safe areas
  half: 50,         // 50vh - half screen
  expanded: 80,     // 80vh - reduced from 85vh to prevent going too high (account for nav)
};

export function useDrawerSheet(
  initialState: DrawerPosition = 'collapsed',
  customHeights?: DrawerHeights
) {
  const DRAWER_HEIGHTS = customHeights || DEFAULT_DRAWER_HEIGHTS;
  const [drawerPosition, setDrawerPosition] = useState<DrawerPosition>(initialState);
  const [isDragging, setIsDragging] = useState(false);
  const [dragHeightPercent, setDragHeightPercent] = useState<number>(DRAWER_HEIGHTS[initialState]);
  
  const startY = useRef(0);
  const startHeight = useRef(DRAWER_HEIGHTS[initialState]);

  const expand = useCallback(() => {
    setDrawerPosition('expanded');
    setDragHeightPercent(DRAWER_HEIGHTS.expanded);
  }, []);

  const collapse = useCallback(() => {
    setDrawerPosition('collapsed');
    setDragHeightPercent(DRAWER_HEIGHTS.collapsed);
  }, []);

  const setHalf = useCallback(() => {
    setDrawerPosition('half');
    setDragHeightPercent(DRAWER_HEIGHTS.half);
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    startY.current = e.touches[0].clientY;
    startHeight.current = DRAWER_HEIGHTS[drawerPosition];
    setIsDragging(true);
  }, [drawerPosition]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = startY.current - currentY; // Positive = swipe up
    const windowHeight = window.innerHeight;
    
    // Calculate new height as percentage
    const deltaPercent = (deltaY / windowHeight) * 100;
    const newHeight = startHeight.current + deltaPercent;
    
    // Clamp between min and max
    const clampedHeight = Math.min(80, Math.max(10, newHeight));
    setDragHeightPercent(clampedHeight);
    
    // Prevent default to avoid page scroll during drag
    e.preventDefault();
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    const currentHeight = dragHeightPercent;
    
    // Find closest snap point using symmetric zones
    // Midpoints: (21+50)/2 = 35.5, (50+80)/2 = 65
    if (currentHeight < 35.5) {
      // Closer to collapsed (21vh)
      setDrawerPosition('collapsed');
      setDragHeightPercent(DRAWER_HEIGHTS.collapsed);
    } else if (currentHeight < 65) {
      // Closer to half (50vh)
      setDrawerPosition('half');
      setDragHeightPercent(DRAWER_HEIGHTS.half);
    } else {
      // Closer to expanded (80vh)
      setDrawerPosition('expanded');
      setDragHeightPercent(DRAWER_HEIGHTS.expanded);
    }
  }, [isDragging, dragHeightPercent]);

  const getDrawerHeight = useCallback(() => {
    return isDragging ? dragHeightPercent : DRAWER_HEIGHTS[drawerPosition];
  }, [isDragging, dragHeightPercent, drawerPosition]);

  const dragHandlers = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  };

  return {
    drawerPosition,
    drawerHeight: getDrawerHeight(),
    isDragging,
    expand,
    collapse,
    setHalf,
    dragHandlers,
  };
}

